import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axiosConfig";
import axios from "axios";
import { initializeSocket, getSocket, disconnectSocket } from "../utils/socket";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoutMessage, setLogoutMessage] = useState(null);
  const [loginMessage, setLoginMessage] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [skipMessage, setSkipMessage] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    console.log("Logging out user");
    setUser(null);
    setToken(null);
    setError(null);
    setNotifications([]);
    setUnreadNotificationCount(0);
    setLoginMessage(null);
    localStorage.clear();
    disconnectSocket();
    setIsFadingOut(false);
    setLogoutMessage("Đăng xuất thành công!");
    setTimeout(() => {
      setIsFadingOut(true);
      setTimeout(() => {
        setLogoutMessage(null);
        setIsFadingOut(false);
        navigate("/login");
      }, 300);
    }, 1500);
  }, [navigate]);

  const refreshToken = useCallback(async () => {
    try {
      const storedRefreshToken = localStorage.getItem("refreshToken");
      console.log("Attempting to refresh token with refresh token:", storedRefreshToken);
      if (!storedRefreshToken) {
        throw new Error("Không có refresh token để làm mới!");
      }
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/refresh-token`,
        { refreshToken: storedRefreshToken },
        { timeout: 10000 }
      );
      const { accessToken: newToken, refreshToken: newRefreshToken } = response.data;
      console.log("New token refreshed successfully:", newToken);
      setToken(newToken);
      localStorage.setItem("accessToken", newToken);
      localStorage.setItem("refreshToken", newRefreshToken);
      initializeSocket(newToken);
      return newToken;
    } catch (error) {
      console.error("Lỗi làm mới token:", error.response ? error.response.data : error.message);
      setError(
        error.response?.data?.message || "Không thể làm mới phiên làm việc. Vui lòng đăng nhập lại."
      );
      setTimeout(() => logout(), 2000);
      throw error;
    }
  }, [logout]);

  useEffect(() => {
    if (!token || !user) return;

    const socket = getSocket();
    if (!socket) {
      console.warn("Socket chưa sẵn sàng, bỏ qua kết nối WebSocket");
      return;
    }

    if (!socket.connected) {
      socket.connect();
    }

    const handleConnect = () => {
      console.log("Đã kết nối tới server WebSocket:", socket.id);
      socket.emit("joinRoom", user._id);
    };

    socket.on("connect", handleConnect);
    socket.on("reconnect", handleConnect);

    socket.on("disconnect", (reason) => {
      console.log("Ngắt kết nối WebSocket:", reason);
    });

    socket.on("connect_error", (err) => {
      console.error("Lỗi kết nối WebSocket:", err.message);
      setError("Không thể kết nối tới server thông báo. Vui lòng thử lại!");
    });

    socket.on("auth_error", async ({ message }) => {
      console.error("Lỗi xác thực Socket.IO:", message);
      try {
        const newToken = await refreshToken();
        socket.auth.token = `Bearer ${newToken}`;
        socket.connect();
      } catch (error) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
        setTimeout(() => logout(), 2000);
      }
    });

    socket.on("notification", (notification) => {
      if (notification.type === "account_banned") {
        setError(notification.message);
        setTimeout(() => logout(), 2000);
        return;
      }

      setNotifications((prev) => {
        const isDuplicate = prev.some(
          (n) =>
            (n._id && n._id === notification._id) ||
            (!n._id && n.message === notification.message && n.createdAt === notification.createdAt)
        );
        if (!isDuplicate) {
          setUnreadNotificationCount((prev) => prev + 1);
          return [notification, ...prev];
        }
        return prev;
      });
    });

    return () => {
      socket.off("connect", handleConnect);
      socket.off("reconnect", handleConnect);
      socket.off("disconnect");
      socket.off("connect_error");
      socket.off("auth_error");
      socket.off("notification");
    };
  }, [token, user, logout, refreshToken]);

  const fetchWithRetry = useCallback(
    async (apiCall, retries = 2, delay = 1000) => {
      for (let i = 0; i <= retries; i++) {
        try {
          const config = apiCall.config || {};
          console.log(`Sending request to ${apiCall.url} with token: ${token || "none"}`);
          const response = await api({
            ...config,
            ...apiCall,
            headers: {
              ...apiCall.headers,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            timeout: 10000,
          });
          console.log(`Request to ${apiCall.url} succeeded:`, response.data);
          return response;
        } catch (error) {
          console.error(`Request failed for ${apiCall.url}:`, error.response ? error.response.data : error.message);
          if (i === retries) {
            if (error.code === "ECONNABORTED") {
              throw new Error("Yêu cầu hết thời gian. Vui lòng kiểm tra kết nối của bạn.");
            }
            const errorMessage =
              error.response?.status === 500
                ? "Server gặp sự cố, vui lòng thử lại sau."
                : error.response?.data?.message || "Lỗi không xác định. Vui lòng thử lại sau.";
            const errorWithStatus = new Error(errorMessage);
            errorWithStatus.response = error.response;
            throw errorWithStatus;
          }
          if (error.response?.status === 401 && token) {
            console.log("Attempting to refresh token due to 401 error");
            try {
              const newToken = await refreshToken();
              setToken(newToken); // Cập nhật token trong state
              apiCall.headers = { ...apiCall.headers, Authorization: `Bearer ${newToken}` };
              console.log("Retrying with new token:", newToken);
            } catch (refreshError) {
              setError(refreshError.message || "Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
              setTimeout(() => logout(), 2000);
              throw refreshError;
            }
          } else if (error.response?.status === 403) {
            const errorMessage =
              error.response?.data?.message ||
              "Bạn không có quyền truy cập tài nguyên này. Vui lòng liên hệ quản trị viên.";
            console.warn(`403 error details for ${apiCall.url}:`, error.response?.data);
            const errorWithStatus = new Error(errorMessage);
            errorWithStatus.response = error.response;
            throw errorWithStatus;
          } else if (error.response?.status === 400 || error.response?.status === 404) {
            const errorMessage =
              error.response?.data?.message || "Yêu cầu không hợp lệ hoặc tài nguyên không tồn tại.";
            const errorWithStatus = new Error(errorMessage);
            errorWithStatus.response = error.response;
            throw errorWithStatus;
          }
          console.log(`Retry attempt ${i + 1}/${retries}...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    },
    [refreshToken, logout, token]
  );

  useEffect(() => {
    if (!token || !user) return;

    const fetchNotifications = async () => {
      try {
        const res = await fetchWithRetry({
          method: "get",
          url: "/api/users/notifications",
        });
        setNotifications(res.data || []);
        const unread = res.data.filter((n) => !n.read).length;
        setUnreadNotificationCount(unread);
      } catch (error) {
        console.error("Lỗi khi lấy thông báo:", error);
        setError("Không thể tải thông báo. Vui lòng thử lại!");
      }
    };

    fetchNotifications();
  }, [token, user, fetchWithRetry]);

  const login = useCallback(
    (userData, accessToken, refreshToken) => {
      console.log("Logging in user:", userData);
      localStorage.clear();
      setUser(null);
      setToken(null);
      setNotifications([]);
      setUnreadNotificationCount(0);
      setError(null);
      setLoginMessage(null);
      setUser({
        ...userData,
        permissions: userData.permissions || [],
      });
      setToken(accessToken);
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("role", userData.role);
      localStorage.setItem("name", userData.name);
      localStorage.setItem("email", userData.email);
      localStorage.setItem("permissions", JSON.stringify(userData.permissions || []));
      initializeSocket(accessToken);
      setIsFadingOut(false);
      setLoginMessage(`Đăng nhập thành công với ${userData.name}!`);
      const clearMessage = () => {
        setIsFadingOut(true);
        setTimeout(() => {
          setLoginMessage(null);
          setIsFadingOut(false);
        }, 300);
      };

      if (skipMessage) {
        clearMessage();
      } else {
        setTimeout(clearMessage, 1500);
      }

      setIsLoading(false);
    },
    [skipMessage]
  );

  const initializeAuth = useCallback(async () => {
    const storedToken = localStorage.getItem("accessToken");
    const storedRole = localStorage.getItem("role");
    const storedName = localStorage.getItem("name");
    const storedEmail = localStorage.getItem("email");
    const storedPermissions = JSON.parse(localStorage.getItem("permissions") || "[]");

    if (!storedToken) {
      setUser(null);
      setToken(null);
      setIsLoading(false);
      return;
    }

    try {
      console.log("Initializing auth with token:", storedToken);
      const response = await fetchWithRetry({
        method: "get",
        url: "/api/users/profile",
      });
      const userData = response.data;
      if (!userData || !userData._id) {
        throw new Error("Dữ liệu người dùng không hợp lệ từ server.");
      }
      setUser({
        ...userData,
        role: userData.role || storedRole,
        name: userData.name || storedName,
        email: userData.email || storedEmail,
        permissions: userData.permissions || storedPermissions,
      });
      setToken(storedToken);
      initializeSocket(storedToken);
    } catch (error) {
      console.error("Initialize auth failed:", error.response ? error.response.data : error.message);
      setError(error.message || "Không thể khởi tạo xác thực. Vui lòng đăng nhập lại.");
      localStorage.clear();
      setUser(null);
      setToken(null);
      setTimeout(() => logout(), 2000);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithRetry, logout]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const value = useMemo(
    () => ({
      user,
      token,
      setToken,
      login,
      logout,
      fetchWithRetry,
      isLoading,
      error,
      logoutMessage,
      loginMessage,
      paymentMessage,
      setPaymentMessage,
      setSkipMessage,
      notifications,
      unreadNotificationCount,
      setNotifications,
      setUnreadNotificationCount,
    }),
    [
      user,
      token,
      login,
      logout,
      fetchWithRetry,
      isLoading,
      error,
      logoutMessage,
      loginMessage,
      paymentMessage,
      notifications,
      unreadNotificationCount,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {(logoutMessage || loginMessage || paymentMessage || error) && (
        <div
          className={`fixed top-4 right-4 text-white px-4 py-2 rounded-lg shadow-lg z-50 ${
            isFadingOut ? "animate-fade-out" : "animate-slide-in"
          } ${loginMessage || paymentMessage ? "bg-green-500" : "bg-red-500"} flex items-center space-x-2`}
        >
          <span>{logoutMessage || loginMessage || paymentMessage || error}</span>
          {(loginMessage || logoutMessage || paymentMessage) && (
            <button
              onClick={() => setSkipMessage(true)}
              className="text-white underline hover:text-gray-200"
            >
              Bỏ qua
            </button>
          )}
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);